import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { 
  BuildingOfficeIcon, 
  CreditCardIcon,
  CubeIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowLeftIcon,
  DevicePhoneMobileIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CogIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import AnimatedImage from '../ui/AnimatedImage';
import bookingImg from '../../assets/booking.png';
import resturantImg from '../../assets/resturant.png';
import dahboardImg from '../../assets/dahboard.png';
import staygraphImg from '../../assets/staygraph.png';

// Separate Modal Components to prevent recreation
const DemoModal = React.memo(({ 
  showDemoModal, 
  setShowDemoModal, 
  demoFormData, 
  handleDemoFormChange, 
  handleDemoSubmit, 
  isSubmitting 
}) => {
  if (!showDemoModal) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50"
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl"
        >
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Request Demo</h3>
            <button
              onClick={() => setShowDemoModal(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          <form onSubmit={handleDemoSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
              <input
                type="text"
                name="name"
                required
                value={demoFormData.name}
                onChange={handleDemoFormChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter your full name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
              <input
                type="email"
                name="email"
                required
                value={demoFormData.email}
                onChange={handleDemoFormChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
              <input
                type="text"
                name="company"
                value={demoFormData.company}
                onChange={handleDemoFormChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter your company name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={demoFormData.phone}
                onChange={handleDemoFormChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter your phone number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Product Interest</label>
              <input
                type="text"
                name="product"
                value={demoFormData.product}
                onChange={handleDemoFormChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Which product are you interested in?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Additional Message</label>
              <textarea
                name="message"
                rows={3}
                value={demoFormData.message}
                onChange={handleDemoFormChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-none"
                placeholder="Tell us about your needs..."
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-200 ${
                isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white hover:shadow-lg transform hover:-translate-y-0.5'
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Submitting...</span>
                </div>
              ) : (
                'Request Demo'
              )}
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
});

const ContactModal = React.memo(({ 
  showContactModal, 
  setShowContactModal, 
  contactFormData, 
  handleContactFormChange, 
  handleContactSubmit, 
  isSubmitting 
}) => {
  if (!showContactModal) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50"
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl"
        >
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Contact Sales</h3>
            <button
              onClick={() => setShowContactModal(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          <form onSubmit={handleContactSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
              <input
                type="text"
                name="name"
                required
                value={contactFormData.name}
                onChange={handleContactFormChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter your full name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
              <input
                type="email"
                name="email"
                required
                value={contactFormData.email}
                onChange={handleContactFormChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
              <input
                type="text"
                name="company"
                value={contactFormData.company}
                onChange={handleContactFormChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter your company name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={contactFormData.phone}
                onChange={handleContactFormChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter your phone number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
              <textarea
                name="message"
                rows={4}
                required
                value={contactFormData.message}
                onChange={handleContactFormChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-none"
                placeholder="Tell us about your requirements..."
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-200 ${
                isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white hover:shadow-lg transform hover:-translate-y-0.5'
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Sending...</span>
                </div>
              ) : (
                'Send Message'
              )}
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
});

const SuccessDialog = React.memo(({ 
  showSuccessDialog, 
  setShowSuccessDialog, 
  successMessage 
}) => {
  if (!showSuccessDialog) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50"
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl"
        >
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mb-6">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Success!
            </h3>
            
            <p className="text-gray-600 mb-6">
              {successMessage}
            </p>
            
            <button
              onClick={() => setShowSuccessDialog(false)}
              className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-3 px-6 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
            >
              Got it, thanks!
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
});

export default function Products() {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchParams] = useSearchParams();
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [demoFormData, setDemoFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    product: '',
    message: ''
  });
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const products = useMemo(() => [
    {
      id: 'hotel-management',
      name: 'Hotel Management System',
      tagline: 'Unifying Hotel Operations Effortlessly',
      description: 'A trusted, robust hotel ERP powering 50+ successful brands. Guests love it, managers rely on it.',
      longDescription: 'From reservations & check-ins to full analytics and billing, our hotel management system delivers seamless, all-in-one operations control. Trusted by leading hotels for its reliability and real-time, error-free execution, it is the backbone for fast-growing businesses.',
      features: [
        'Reservation & booking calendar',
        'Front desk check-in/check-out',
        'Automated billing & folio',
        'Analytics dashboard',
        'Housekeeping & maintenance',
        'Customizable guest journeys',
        'Role-based access',
        'Multi-property support'
      ],
      benefits: [
        'Proven at 50+ properties, 99.99% uptime',
        'Boost guest satisfaction & positive reviews',
        'Zero manual reporting errors',
        'Reduces training time for staff',
        'Rapid setup, instant cloud backup'
      ],
      platforms: ['Web Dashboard', 'Mobile App'],
      icon: BuildingOfficeIcon,
      image: bookingImg,
      color: 'from-green-500 to-emerald-500',
      status: 'available',
      demoUrl: '#contact'
    },
    {
      id: 'restaurant-management',
      name: 'Restaurant Management System',
      tagline: 'Accelerate Dining Excellence',
      description: 'Elevating food & beverage operations from order to kitchen to bill—speed and trust, in every transaction.',
      longDescription: 'Efficient POS, digital menus, kitchen screens, table management, and billing, all-in-one. Used actively with renowned restaurant chains to streamline operations, reduce waste, and delight customers.',
      features: [
        'Fast digital order taking',
        'Table and queue management',
        'Integrated kitchen display systems',
        'Menu and pricing controls',
        'Split/merge bills',
        'Inventory sync with F&B',
        'Realtime sales reports',
        'Waiter/chef notifications'
      ],
      benefits: [
        'Visible performance at 30+ top restaurants',
        'Speedy, error-free kitchen communication',
        'Smart ingredient & cost reduction',
        'Easy staff onboarding',
        'Smooth GST billing & compliance'
      ],
      platforms: ['Web POS', 'Kitchen Display', 'Tablet App'],
      icon: CubeIcon,
      image: resturantImg,
      color: 'from-yellow-400 to-orange-500',
      status: 'available',
      demoUrl: '#contact'
    },
    {
      id: 'inventory-management',
      name: 'Inventory Management System',
      tagline: 'Reliable Stock Mastery',
      description: 'Live, lossless stock control in hospitality and retail. Trusted by thriving hotels and chains.',
      longDescription: 'Seamlessly track goods, automate reorders, and manage distributed warehouses with deep analytics. Used across multiple industries to maintain perfect inventory levels and cut waste.',
      features: [
        'Item-level, real-time tracking',
        'Auto reorder alerts',
        'Multi-location integration',
        'Barcode/QR scanning',
        'Supplier & vendor tracking',
        'Stock movement audit logs',
        'Expiry & wastage alerts',
        'Inventory charts & forecasting'
      ],
      benefits: [
        'Eliminates stockouts/shrinkage',
        'Cuts costs through precise reordering',
        '5+ years proven with Indian hoteliers & shops',
        'Integrates with hotel & restaurant ERPs',
        'Hands-off compliance support'
      ],
      platforms: ['Web Dashboard', 'Mobile App', 'Barcode Scanner'],
      icon: CreditCardIcon,
      image: dahboardImg,
      color: 'from-orange-500 to-red-500',
      status: 'available',
      demoUrl: '#contact'
    },
    {
      id: 'billing-system',
      name: 'Billing System',
      tagline: 'Simple, Powerful Billing Engine',
      description: 'Easy GST-compliant bills, payment tracking, multi-outlet reporting. Revenue confidence for every business.',
      longDescription: 'Automate invoice creation, manage payment statuses, and integrate seamlessly with your business processes. Ensures quick collections and regulatory compliance.',
      features: [
        'GST invoice automation',
        'Multiple payment support',
        'Easy reconciliations',
        'Custom bill templates',
        'Mobile-friendly POS billing',
        'Auto email or SMS bills',
        'Multi-outlet view',
        'Role-permission controls'
      ],
      benefits: [
        'Zero missed payments',
        'Works everywhere—mobile, web, POS',
        'Loved by accountants, trusted by owners',
        'No more GST filing headaches',
        'Digital-first, eco-friendly receipts'
      ],
      platforms: ['Web POS', 'Mobile App'],
      icon: ChartBarIcon,
      image: dahboardImg,
      color: 'from-purple-500 to-pink-500',
      status: 'available',
      demoUrl: '#contact'
    },
    {
      id: 'staff-management',
      name: 'Staff Management',
      tagline: 'Empower Teams, Automate HR',
      description: 'Staff scheduling, attendance, and roles—used for 1,000+ employees across 40+ businesses.',
      longDescription: 'Simplifies roster creation, automates attendance, and streamlines payroll/pre-compliance checks. Ensures managers have full control and staff are kept happy.',
      features: [
        'Smart scheduling & shifts',
        'Leave management/requests',
        'Biometric attendance sync',
        'Automated payroll exports',
        'Document vault for staff',
        'Role management',
        'Chat and noticeboard','Quick onboarding forms'
      ],
      benefits: [
        'Reduced HR workload by 60%',
        'Extremely reliable for multi-site teams',
        'No attendance disputes',
        'Employee engagement boost',
        'Field-tested with growing businesses'
      ],
      platforms: ['Web Dashboard', 'Mobile App'],
      icon: CogIcon,
      image: dahboardImg,
      color: 'from-blue-500 to-blue-700',
      status: 'available',
      demoUrl: '#contact'
    },
    {
      id: 'visitor-card-scanner',
      name: 'Visitor Card Scanner',
      tagline: 'Fast, Secure Guest Entry Recording',
      description: 'Instantly logs guests, vendors, suppliers with smart scanner. Powers safer, smarter entry management.',
      longDescription: 'Innovative card (QR/RFID/barcode) scanning at entry points for secure, automated guest/vendor recording. Used at busy sites for audit-trail compliance and fast security checks.',
      features: [
        'QR, RFID, barcode support',
        'Photo capture & visitor logs',
        'Host notifications',
        'Mobile check-in/out',
        'Custom badge printing',
        'Blacklist/whitelist controls',
        'Realtime data dashboard',
        'Automatic compliance downloads'
      ],
      benefits: [
        'Used at high-traffic hotels & business parks',
        'Boosts onsite safety',
        'Contactless & paperless',
        'Optimized for busy receptions',
        'Audit-ready compliance reports'
      ],
      platforms: ['Mobile Scanner', 'Web Dashboard'],
      icon: ShieldCheckIcon,
      image: staygraphImg || bookingImg,
      color: 'from-gray-800 to-gray-600',
      status: 'available',
      demoUrl: '#contact'
    },
  ], []);

  // Check for product parameter in URL on component mount
  useEffect(() => {
    const productId = searchParams.get('product');
    if (productId) {
      const product = products.find(p => p.id === productId);
      if (product) {
        setSelectedProduct(product);
        // Scroll to top when opening product details
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [searchParams, products]);

  const handleLearnMore = (product) => {
    setSelectedProduct(product);
    // Scroll to top when opening product details
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setSelectedProduct(null);
  };

  const handleDemoRequest = (product = null) => {
    if (product) {
      setDemoFormData(prev => ({ ...prev, product: product.name }));
    }
    setShowDemoModal(true);
  };

  const handleContactSales = () => {
    setShowContactModal(true);
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSuccessMessage('Demo request submitted successfully! Our team will contact you within 24 hours to schedule your personalized demo.');
      setShowSuccessDialog(true);
      setShowDemoModal(false);
      setDemoFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        product: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting demo request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSuccessMessage('Message sent successfully! Our sales team will get back to you within 24 hours.');
      setShowSuccessDialog(true);
      setShowContactModal(false);
      setContactFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDemoFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setDemoFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleContactFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setContactFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  if (selectedProduct) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <DemoModal showDemoModal={showDemoModal} setShowDemoModal={setShowDemoModal} demoFormData={demoFormData} handleDemoFormChange={handleDemoFormChange} handleDemoSubmit={handleDemoSubmit} isSubmitting={isSubmitting} />
        <ContactModal showContactModal={showContactModal} setShowContactModal={setShowContactModal} contactFormData={contactFormData} handleContactFormChange={handleContactFormChange} handleContactSubmit={handleContactSubmit} isSubmitting={isSubmitting} />
        <SuccessDialog showSuccessDialog={showSuccessDialog} setShowSuccessDialog={setShowSuccessDialog} successMessage={successMessage} />
        
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-12">
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={handleBack}
            className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 mb-8 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            <span>Back to Products</span>
          </motion.button>

          {/* Product Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-xl p-8 mb-12"
          >
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center space-x-4 mb-6">
                  <div className={`flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-r ${selectedProduct.color}`}>
                    <selectedProduct.icon className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-4xl font-bold text-gray-900">{selectedProduct.name}</h1>
                    <p className="text-xl text-gray-600">{selectedProduct.tagline}</p>
                  </div>
                </div>
                
                <p className="text-lg text-gray-700 mb-6">{selectedProduct.longDescription}</p>
                
                <div className="flex items-center space-x-4 mb-6">
                  {selectedProduct.status === 'available' ? (
                    <div className="flex items-center space-x-2">
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      <span className="text-green-600 font-medium">Available Now</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <ClockIcon className="h-5 w-5 text-accent-500" />
                      <span className="text-accent-600 font-medium">Coming Soon</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  {selectedProduct.status === 'available' ? (
                    <button
                      onClick={handleContactSales}
                      className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
                    >
                      Contact Sales
                    </button>
                  ) : (
                    <button
                      onClick={handleContactSales}
                      className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
                    >
                      Contact Sales
                    </button>
                  )}
                  <button
                    onClick={handleBack}
                    className="border-2 border-primary-600 text-primary-600 px-8 py-3 rounded-xl font-semibold hover:bg-primary-50 transition-all duration-200"
                  >
                    View All Products
                  </button>
                </div>
              </div>
              
              <div className="flex justify-center">
                <AnimatedImage
                  src={selectedProduct.image}
                  alt={`${selectedProduct.name} Screenshot`}
                  animationType="zoomIn"
                  className="w-full max-w-lg rounded-2xl shadow-2xl border border-gray-100"
                />
              </div>
            </div>
          </motion.div>

          {/* Product Details */}
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Features */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Features</h2>
              <div className="space-y-4">
                {selectedProduct.features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Business Benefits</h2>
              <div className="space-y-4">
                {selectedProduct.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Platform Support */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-12 bg-white rounded-2xl shadow-lg p-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Platforms</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {selectedProduct.platforms.map((platform, index) => (
                <div key={index} className="text-center">
                  <div className="flex justify-center mb-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-primary-600 to-secondary-600">
                      <DevicePhoneMobileIcon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <span className="text-sm font-medium text-gray-700">{platform}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <DemoModal showDemoModal={showDemoModal} setShowDemoModal={setShowDemoModal} demoFormData={demoFormData} handleDemoFormChange={handleDemoFormChange} handleDemoSubmit={handleDemoSubmit} isSubmitting={isSubmitting} />
      <ContactModal showContactModal={showContactModal} setShowContactModal={setShowContactModal} contactFormData={contactFormData} handleContactFormChange={handleContactFormChange} handleContactSubmit={handleContactSubmit} isSubmitting={isSubmitting} />
      <SuccessDialog showSuccessDialog={showSuccessDialog} setShowSuccessDialog={setShowSuccessDialog} successMessage={successMessage} />
      
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Our Complete Product Suite
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover our comprehensive range of technology solutions designed to transform 
            your hospitality and business operations.
          </p>
        </motion.div>

        {/* Products Grid */}
        <div className="grid gap-8 lg:grid-cols-2">
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
            >
              <div className="p-8">
                <div className="flex items-center space-x-4 mb-6">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r ${product.color}`}>
                    <product.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{product.name}</h3>
                    <p className="text-gray-600">{product.tagline}</p>
                  </div>
                </div>
                
                <p className="text-gray-700 mb-6">{product.description}</p>
                
                <div className="flex items-center mb-6">
                  <div className="flex items-center space-x-2">
                    {product.status === 'available' ? (
                      <>
                        <CheckCircleIcon className="h-4 w-4 text-green-500" />
                        <span className="text-sm text-green-600 font-medium">Available</span>
                      </>
                    ) : (
                      <>
                        <ClockIcon className="h-4 w-4 text-accent-500" />
                        <span className="text-sm text-accent-600 font-medium">Coming Soon</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => handleLearnMore(product)}
                    className="flex-1 bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-3 px-6 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
                  >
                    Learn More
                  </button>
                </div>
              </div>
              
              {product.image && (
                <div className="p-8 pt-0">
                  <AnimatedImage
                    src={product.image}
                    alt={`${product.name} Screenshot`}
                    animationType="zoomIn"
                    className="w-full rounded-xl shadow-md border border-gray-100"
                  />
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Clients Live Sites Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-16"
        >
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">See Our Clients: Live Sites</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">These businesses are powered by our solutions and are leaders in their industries.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 flex flex-col items-center">
              <span className="text-2xl font-bold text-primary-600 mb-2">Bigtekk</span>
              <p className="text-gray-600 mb-4 text-center">A regional technology leader using our full software suite for operational success.</p>
              <a target="_blank" rel="noopener noreferrer" href="https://bigtekk.in" className="mt-auto inline-block bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-6 py-2 rounded-xl font-semibold transition-all duration-200 hover:from-primary-700 hover:to-secondary-700">Visit Website</a>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 flex flex-col items-center">
              <span className="text-2xl font-bold text-primary-600 mb-2">Nandiksha</span>
              <p className="text-gray-600 mb-4 text-center">A trusted hospitality name leveraging our hotel & staff management systems for guest delight.</p>
              <a target="_blank" rel="noopener noreferrer" href="https://nandiksha.in/" className="mt-auto inline-block bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-6 py-2 rounded-xl font-semibold transition-all duration-200 hover:from-primary-700 hover:to-secondary-700">Visit Website</a>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 flex flex-col items-center">
              <span className="text-2xl font-bold text-primary-600 mb-2">Banoc</span>
              <p className="text-gray-600 mb-4 text-center">One of the fastest-growing enterprises, efficiently managed with our inventory & billing tech.</p>
              <a target="_blank" rel="noopener noreferrer" href="https://www.gobanoc.com/" className="mt-auto inline-block bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-6 py-2 rounded-xl font-semibold transition-all duration-200 hover:from-primary-700 hover:to-secondary-700">Visit Website</a>
            </div>
          </div>
        </motion.div>
        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-3xl p-8 sm:p-12">
            <h2 className="text-2xl font-bold text-white mb-4">
              Ready to Transform Your Business?
            </h2>
            <p className="text-primary-100 mb-8 max-w-2xl mx-auto">
              Join hundreds of businesses already using Autiin solutions to streamline their operations 
              and boost their bottom line.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => handleDemoRequest()}
                className="bg-white text-primary-600 px-8 py-3 rounded-xl font-semibold hover:bg-gray-50 transform hover:-translate-y-0.5 transition-all duration-200"
              >
                Request Demo
              </button>
              <button
                onClick={handleContactSales}
                className="border-2 border-white text-white px-8 py-3 rounded-xl font-semibold hover:bg-white hover:text-primary-600 transform hover:-translate-y-0.5 transition-all duration-200"
              >
                Contact Sales
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
} 